import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, Type } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { KatexOptions, MarkdownModule } from 'ngx-markdown';
import { AccordionBlock, AccordionPanel } from '../../../model';
import {
  getIconBgColor,
  parseAccordionIcon,
  ParsedAccordionIcon,
} from '../../../editor/extensions/accordion/accordion-icon.util';

@Component({
  selector: 'dojo-accordion-block-renderer',
  template: `
    <mat-accordion class="custom-mat-accordion mat-accordion--faq mat-accordion" [multi]="true">
      @for (panel of block().panels; track $index) {
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>
              @if (getParsedIcon(panel); as pi) {
                <span
                  class="mr-2"
                  [style.display]="'inline-flex'"
                  [style.align-items]="'center'"
                  [style.justify-content]="'center'"
                  [style.font-size]="'1rem'"
                  [style.width]="pi.showBackground ? '28px' : '20px'"
                  [style.height]="pi.showBackground ? '28px' : '20px'"
                  [style.border-radius]="pi.showBackground ? '6px' : null"
                  [style.background-color]="pi.showBackground ? getIconBgColor(pi.color) : null"
                >
                  @switch (pi.type) {
                    @case ('emoji') {
                      {{ pi.value }}
                    }
                    @case ('fa') {
                      <i [class]="'fas fa-' + pi.value" [style.color]="pi.color" style="font-size: 16px"></i>
                    }
                    @case ('img') {
                      <img [src]="pi.value" alt="" style="width: 18px; height: 18px; object-fit: contain" />
                    }
                  }
                </span>
              }
              <markdown
                class="accordion-title-markdown"
                [data]="panel.title"
                [inline]="true"
                katex
                [katexOptions]="katexOptions"
              />
            </mat-panel-title>
          </mat-expansion-panel-header>
          <div class="p-4">
            <ng-container *ngComponentOutlet="contentRenderer(); inputs: { blocks: panel.content }" />
          </div>
        </mat-expansion-panel>
      }
    </mat-accordion>
  `,
  styles: `
    :host {
      display: block;
    }

    :host ::ng-deep .custom-mat-accordion.mat-accordion .mat-content,
    :host ::ng-deep .custom-mat-accordion.mat-accordion .mat-panel-title,
    :host ::ng-deep .custom-mat-accordion.mat-accordion .mat-expansion-panel-header-title {
      min-width: 0;
      overflow-x: visible !important;
    }

    :host ::ng-deep .custom-mat-accordion.mat-accordion .mat-expansion-panel-header-title {
      overflow-y: hidden !important;
    }

    :host ::ng-deep .custom-mat-accordion.mat-accordion .accordion-title-markdown {
      display: inline;
      max-width: 100%;
      min-width: 0;
      overflow: visible !important;
    }

    :host ::ng-deep .custom-mat-accordion.mat-accordion .accordion-title-markdown p {
      margin: 0;
      display: inline;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatExpansionModule, NgComponentOutlet, MarkdownModule],
})
export class AccordionBlockRendererComponent {
  readonly block = input.required<AccordionBlock>();
  readonly contentRenderer = input.required<Type<unknown>>();
  readonly katexOptions: KatexOptions = {
    throwOnError: false,
    strict: 'warn',
  };

  getParsedIcon(panel: AccordionPanel): ParsedAccordionIcon | null {
    return panel.icon ? parseAccordionIcon(panel.icon) : null;
  }

  getIconBgColor(color?: string): string | null {
    return getIconBgColor(color);
  }
}
